import { ExpandMore } from '@mui/icons-material';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { ClipLoader } from 'react-spinners';
import { Site, SiteType, useGetSitesQuery } from '../../generated/graphql';
import { useState, useMemo } from 'react';
import { colors } from '../../styles';
import { useThemeSelector } from '../contexts/ThemeContext';
import { t } from 'i18next';
import { ParkingMap } from '../components/ParkingMap';

export const useSiteSelector = (
  qrCode: string | undefined,
  setDuration: (duration: number) => void,
  setTimeSteps: (timeSteps: number) => void
) => {
  const [location, setLocation] = useState<string | number>(qrCode ?? '');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedSiteType, setSelectedSiteType] = useState<SiteType | 'ALL'>('ALL');
  const { data: sites, loading: sitesLoading } = useGetSitesQuery();
  const { setSelectedTheme } = useThemeSelector();

  const activeSiteNotifications = sites?.activeSiteNotifications ?? [];

  // Filter sites based on selected site type
  const filteredSites = useMemo(() => {
    if (!sites?.getSites) return [];
    if (selectedSiteType === 'ALL') return sites.getSites;
    return sites.getSites.filter((site) => site.siteType === selectedSiteType);
  }, [sites?.getSites, selectedSiteType]);

  const handleChange = (event: SelectChangeEvent<typeof location>) => {
    const value = event.target.value;
    setLocation(value);

    if (value === '') {
      setSelectedSite(null);
      return;
    }

    const site = filteredSites.find((x) => x.qrCode === value);
    if (site) {
      setSiteInfo(site);
    }
  };

  const handleSiteTypeChange = (event: SelectChangeEvent<SiteType | 'ALL'>) => {
    const value = event.target.value as SiteType | 'ALL';
    setSelectedSiteType(value);
    // Clear location selection if the currently selected site doesn't match the new filter
    if (selectedSite && value !== 'ALL' && selectedSite.siteType !== value) {
      setLocation('');
      setSelectedSite(null);
    }
  };

  const setSiteInfo = (
    site: Site,
    options?: { preserveDuration?: boolean },
  ) => {
    setSelectedSite(site);
    setLocation(site.qrCode);
    if (!options?.preserveDuration) {
      setDuration(site.minimumTime + (site.hasFreeTime ? site.freeTime : 0));
    }
    setTimeSteps(site.timeStep);
    if (site.siteConfiguration) {
      setSelectedTheme(site.siteConfiguration);
    }
  };

  const SiteTypeFilterComponent: React.FC = () => (
    <FormControl fullWidth>
      <InputLabel id="site-type-label">{t('siteType')}</InputLabel>
      <Select
        labelId="site-type-label"
        value={selectedSiteType}
        label={t('siteType')}
        variant="outlined"
        onChange={handleSiteTypeChange}
        disabled={sitesLoading}
        IconComponent={() => (
          <Box paddingRight={1}>
            {sitesLoading ? (
              <ClipLoader size={'2rem'} color={colors.black} />
            ) : (
              <ExpandMore />
            )}
          </Box>
        )}
      >
        <MenuItem value="ALL">
          {t('allSiteTypes')}
        </MenuItem>
        <MenuItem value={SiteType.CentralPark}>
          {t('centralPark')}
        </MenuItem>
        <MenuItem value={SiteType.Mainland}>
          {t('mainland')}
        </MenuItem>
      </Select>
    </FormControl>
  );

  const SiteSelectorComponent: React.FC<{
    loadedBySpecialTariff: boolean;
    validateRenewalLoading: boolean;
  }> = ({ loadedBySpecialTariff, validateRenewalLoading }) => (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <SiteTypeFilterComponent />
      <FormControl fullWidth>
        <InputLabel id="location-label">{t('location')}</InputLabel>
        <Select
          labelId="location-label"
          value={location}
          label="Location"
          variant="outlined"
          onChange={handleChange}
          disabled={sitesLoading || loadedBySpecialTariff || Boolean(qrCode)}
          IconComponent={() => (
            <Box paddingRight={1}>
              {sitesLoading || validateRenewalLoading ? (
                <ClipLoader size={'2rem'} color={colors.black} />
              ) : (
                <ExpandMore />
              )}
            </Box>
          )}
        >
          <MenuItem value={''}>
            <em>{t('none')}</em>
          </MenuItem>
          {filteredSites.map((site) => (
            <MenuItem value={site.qrCode} key={site.qrCode}>
              {site.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {filteredSites.length > 0 && !location && (
        <ParkingMap
          sites={filteredSites}
          onSiteSelect={setSiteInfo}
          selectedSite={selectedSite}
        />
      )}
    </Stack>
  );
  return {
    selectedSite,
    sitesLoading,
    setSelectedSite,
    SiteSelectorComponent,
    sites,
    activeSiteNotifications,
    setSiteInfo,
    location,
    filteredSites,
  };
};
